import { FC } from 'react'
import { useTranslate } from 'react-admin'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import HubIcon from '@mui/icons-material/Hub'
import StorageIcon from '@mui/icons-material/Storage'
import EditNoteIcon from '@mui/icons-material/EditNote'
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep'
import LibraryAddIcon from '@mui/icons-material/LibraryAdd'
import SearchIcon from '@mui/icons-material/Search'
import ExpressionTextField from '../autocomplete/ExpressionTextField'
import {
  DataStoreMode,
  DataStoreNodeData,
  DataStoreOperation,
  WorkflowNodeData,
} from '../types'
import { themeColor } from '../../../layout/theme/color'

interface DataStoreConfigProps {
  data: DataStoreNodeData
  onUpdate: (data: Partial<WorkflowNodeData>) => void
}

const DataStoreConfig: FC<DataStoreConfigProps> = ({ data, onUpdate }) => {
  const translate = useTranslate()
  const operation: DataStoreOperation = data.operation ?? 'read'
  const isPlain = data.storageMode === 'plain'
  const identifier = (data.identifier ?? '').trim()
  const recordId = (data.recordId ?? '').trim()
  const hasContent = Boolean((data.content ?? '').trim())
  const hasQuery = Boolean((data.query ?? '').trim())
  const keyValue = (data.key ?? '').trim()

  const handleTopK = (raw: string) => {
    const parsed = Number.parseInt(raw, 10)
    if (Number.isNaN(parsed)) {
      onUpdate({ topK: 5 })
      return
    }
    onUpdate({ topK: Math.max(1, Math.min(parsed, 50)) })
  }

  const warningText = (() => {
    if (operation === 'delete') {
      if (isPlain) {
        if (!identifier) return translate('workflow.node.dataStoreMissingIdentifier')
        if (!keyValue) return translate('workflow.node.dataStoreMissingKey')
        return null
      }
      return !recordId ? translate('workflow.node.dataStoreMissingRecordId') : null
    }
    if (operation === 'update') {
      if (!recordId) return translate('workflow.node.dataStoreMissingRecordId')
      if (!hasContent) return translate('workflow.node.dataStoreMissingContent')
      return null
    }
    if (!identifier) return translate('workflow.node.dataStoreMissingIdentifier')
    if (operation === 'write' && isPlain && !keyValue)
      return translate('workflow.node.dataStoreMissingKey')
    if (operation === 'write' && !hasContent)
      return translate('workflow.node.dataStoreMissingContent')
    if (operation === 'read' && !isPlain && !hasQuery)
      return translate('workflow.node.dataStoreMissingQuery')
    return null
  })()

  const showStorageMode =
    operation === 'write' || operation === 'read' || operation === 'delete'
  const showIdentifier =
    operation === 'write' || operation === 'read' || (operation === 'delete' && isPlain)
  const showContent = operation === 'write' || operation === 'update'
  const showRecordId = operation === 'update' || (operation === 'delete' && !isPlain)
  const showKey = (operation === 'write' || operation === 'delete') && isPlain
  const showReadFields = operation === 'read'

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {warningText && <Alert severity="warning">{warningText}</Alert>}

      <Box>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: themeColor.slate60, mb: 0.5, display: 'block' }}
        >
          {translate('workflow.config.dataStoreOperation')}
        </Typography>
        <ToggleButtonGroup
          value={operation}
          exclusive
          onChange={(_e, val: DataStoreOperation | null) => val && onUpdate({ operation: val })}
          size="small"
          fullWidth
        >
          <ToggleButton value="read" sx={{ textTransform: 'none', gap: 0.5 }}>
            <SearchIcon sx={{ fontSize: 16 }} /> {translate('workflow.config.dataStoreOpRead')}
          </ToggleButton>
          <ToggleButton value="write" sx={{ textTransform: 'none', gap: 0.5 }}>
            <LibraryAddIcon sx={{ fontSize: 16 }} /> {translate('workflow.config.dataStoreOpWrite')}
          </ToggleButton>
          <ToggleButton value="update" sx={{ textTransform: 'none', gap: 0.5 }}>
            <EditNoteIcon sx={{ fontSize: 16 }} /> {translate('workflow.config.dataStoreOpUpdate')}
          </ToggleButton>
          <ToggleButton value="delete" sx={{ textTransform: 'none', gap: 0.5 }}>
            <DeleteSweepIcon sx={{ fontSize: 16 }} />{' '}
            {translate('workflow.config.dataStoreOpDelete')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <TextField
        label={translate('workflow.config.label')}
        value={data.label}
        onChange={(e) => onUpdate({ label: e.target.value })}
        size="small"
        fullWidth
      />

      {showStorageMode && (
        <Box>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: themeColor.slate60, mb: 0.5, display: 'block' }}
          >
            {translate('workflow.config.dataStoreMode')}
          </Typography>
          <ToggleButtonGroup
            value={data.storageMode ?? 'vector'}
            exclusive
            onChange={(_e, val: DataStoreMode | null) => val && onUpdate({ storageMode: val })}
            size="small"
            fullWidth
          >
            <ToggleButton value="vector" sx={{ textTransform: 'none', gap: 0.5 }}>
              <HubIcon sx={{ fontSize: 16 }} /> {translate('workflow.config.dataStoreModeVector')}
            </ToggleButton>
            <ToggleButton value="plain" sx={{ textTransform: 'none', gap: 0.5 }}>
              <StorageIcon sx={{ fontSize: 16 }} />{' '}
              {translate('workflow.config.dataStoreModePlain')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {showRecordId && (
        <ExpressionTextField
          required
          error={!recordId}
          label={translate('workflow.config.dataStoreRecordId')}
          value={data.recordId ?? ''}
          onChange={(e) => onUpdate({ recordId: e.target.value })}
          size="small"
          fullWidth
          placeholder="{{ trigger.id }}"
          helperText={translate('workflow.config.dataStoreRecordIdHelp')}
          InputProps={{ sx: { fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: 12 } }}
        />
      )}

      {showIdentifier && (
        <ExpressionTextField
          required
          error={!identifier}
          label={translate('workflow.config.dataStoreIdentifier')}
          value={data.identifier ?? ''}
          onChange={(e) => onUpdate({ identifier: e.target.value })}
          size="small"
          fullWidth
          placeholder="paspoort-faq"
          helperText={translate('workflow.config.dataStoreIdentifierHelp')}
          InputProps={{ sx: { fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: 12 } }}
        />
      )}

      {showKey && (
        <ExpressionTextField
          required
          error={!keyValue}
          label={translate('workflow.config.dataStoreKey')}
          value={data.key ?? ''}
          onChange={(e) => onUpdate({ key: e.target.value })}
          size="small"
          fullWidth
          placeholder="passport_processing_days"
          helperText={translate('workflow.config.dataStoreKeyHelp')}
          InputProps={{ sx: { fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: 12 } }}
        />
      )}

      {showContent && (
        <ExpressionTextField
          required
          error={!hasContent}
          label={translate('workflow.config.dataStoreContent')}
          value={data.content ?? ''}
          onChange={(e) => onUpdate({ content: e.target.value })}
          size="small"
          fullWidth
          multiline
          minRows={4}
          helperText={translate('workflow.config.dataStoreContentHelp')}
        />
      )}

      {showReadFields && (
        <>
          {!isPlain && (
            <ExpressionTextField
              required
              error={!hasQuery}
              label={translate('workflow.config.dataStoreQuery')}
              value={data.query ?? ''}
              onChange={(e) => onUpdate({ query: e.target.value })}
              size="small"
              fullWidth
              multiline
              minRows={3}
              helperText={translate('workflow.config.dataStoreQueryHelp')}
            />
          )}
          {isPlain && (
            <>
              <ExpressionTextField
                label={translate('workflow.config.dataStoreKey')}
                value={data.key ?? ''}
                onChange={(e) => onUpdate({ key: e.target.value })}
                size="small"
                fullWidth
                helperText={translate('workflow.config.dataStoreReadKeyHelp')}
                InputProps={{
                  sx: { fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: 12 },
                }}
              />
              <ExpressionTextField
                label={translate('workflow.config.dataStoreKeyword')}
                value={data.keyword ?? ''}
                onChange={(e) => onUpdate({ keyword: e.target.value })}
                size="small"
                fullWidth
                placeholder="{{ trigger.keyword }}"
                helperText={translate('workflow.config.dataStoreKeywordHelp')}
                InputProps={{
                  sx: { fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: 12 },
                }}
              />
            </>
          )}
          <TextField
            label={translate('workflow.config.dataStoreTopK')}
            type="number"
            value={data.topK ?? 5}
            onChange={(e) => handleTopK(e.target.value)}
            size="small"
            inputProps={{ min: 1, max: 50, step: 1 }}
            helperText={translate('workflow.config.dataStoreTopKHelp')}
          />
        </>
      )}
    </Box>
  )
}

export default DataStoreConfig
